'use client';

import * as React from 'react';
import { useVehicleClaimStats } from '@/hooks/useVehicleClaimStats';
import { Skeleton } from '@/components/ui/skeleton';
import type { ClaimStatusCount, VehicleClaimListFilters } from '@/types/vehicle-claim';

export interface ClaimsStatusTilesProps {
  filters?: VehicleClaimListFilters;
}

// Tenant-configurable status colors are constrained to this palette by the
// admin UI's color picker (ClaimStatusManager.tsx COLOR_OPTIONS) — any other
// value (or a status with no color set) falls back to the neutral entry.
const TILE_COLOR_CLASSES: Record<string, string> = {
  blue: 'border-blue-200 bg-blue-50 text-blue-700',
  yellow: 'border-yellow-200 bg-yellow-50 text-yellow-700',
  green: 'border-green-200 bg-green-50 text-green-700',
  emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  red: 'border-red-200 bg-red-50 text-red-700',
  orange: 'border-orange-200 bg-orange-50 text-orange-700',
  gray: 'border-gray-200 bg-gray-50 text-gray-700',
};

function tileColorClasses(color: string | null | undefined): string {
  return (color && TILE_COLOR_CLASSES[color]) || TILE_COLOR_CLASSES.gray;
}

const GRID_CLASSES = 'grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6';

function TileSkeleton() {
  return (
    <div className="rounded-lg border border-border-primary bg-background-primary p-4">
      <Skeleton className="h-8 w-12" />
      <Skeleton className="mt-2 h-4 w-20" />
    </div>
  );
}

function StatusTile({ status }: { status: ClaimStatusCount }) {
  return (
    <div className={`rounded-lg border p-4 ${tileColorClasses(status.color)}`} role="listitem">
      <p className="text-2xl font-semibold">{status.count}</p>
      <p className="mt-1 text-sm font-medium">{status.label}</p>
    </div>
  );
}

/** Grid of claim-count tiles, one per configured status, driven entirely by `useVehicleClaimStats`'s `by_status`. */
export function ClaimsStatusTiles({ filters }: ClaimsStatusTilesProps) {
  const { data, isLoading, error } = useVehicleClaimStats(filters);

  if (isLoading) {
    return (
      <div className={GRID_CLASSES} aria-label="Loading claim status counts">
        {Array.from({ length: 6 }).map((_, i) => (
          <TileSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-error-200 bg-error-50 p-4 text-sm text-error-700">
        Failed to load claim status counts.
      </div>
    );
  }

  const byStatus = data?.by_status ?? [];

  if (byStatus.length === 0) {
    return (
      <div className="rounded-lg border border-border-primary bg-background-primary p-4 text-sm text-text-secondary">
        No claim statuses configured.
      </div>
    );
  }

  return (
    <div className={GRID_CLASSES} role="list">
      {byStatus.map((status) => (
        <StatusTile key={status.status_code} status={status} />
      ))}
    </div>
  );
}
